/**
 * sl-snippet-sticky-atc-v2 — Sticky Add to Cart bar (BDB redesign)
 *
 * <sticky-atc> is a thin controller that mirrors and drives the MAIN
 * <product-form> already on the page. It never adds to cart on its own:
 *   - shade selection is proxied into the main [data-single-option-selector]
 *     inputs, so all existing ProductForm logic (variant, price, URL, image,
 *     availability) keeps working;
 *   - quantity is kept in sync with the main input[name="quantity"];
 *   - "Add to Cart" fires a click on the main [data-add-to-cart] button.
 *
 * Markup: snippets/sticky-atc-v2.liquid
 */
(function () {
  'use strict';

  class StickyATC extends HTMLElement {
    connectedCallback() {
      // Scope every "main form" lookup to the parent product section.
      this.sectionId = this.dataset.sectionId;
      this.productEl = document.getElementById('Product--' + this.sectionId) || document;

      this.mainAddBtn = this.productEl.querySelector('[data-add-to-cart]');
      this.mainQtyInput = this.productEl.querySelector('input[name="quantity"]');
      this.variantsContainer = this.productEl.querySelector('[data-product-variants]');

      this.shadeSelectors = Array.prototype.slice.call(this.querySelectorAll('[data-shade-selector]'));
      this.qtyValueEl = this.querySelector('[data-qty-value]');
      this.addBtn = this.querySelector('[data-sticky-add]');
      this.addText = this.querySelector('[data-sticky-add-text]');

      this.qty = 1;

      // JS is present → allow the bar to render (CSS keeps it off-screen
      // until .is-visible is toggled).
      this.removeAttribute('hidden');

      this.bindVisibility();
      this.bindShade();
      this.bindQty();
      this.bindAdd();
      this.bindReflect();
      this.syncFromMain();

      this._onDocClick = this._onDocClick.bind(this);
      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      if (this.observer) { this.observer.disconnect(); }
      if (this.btnObserver) { this.btnObserver.disconnect(); }
    }

    /* ------------------------------------------------------------------ *
     * Visibility (show once the main ATC button scrolls out of view)
     * ------------------------------------------------------------------ */
    bindVisibility() {
      var self = this;
      var setHeight = function () {
        document.documentElement.style.setProperty('--sticky-atc-height', self.offsetHeight + 'px');
      };

      if (this.mainAddBtn && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.setVisible(!entry.isIntersecting);
          });
        }, { threshold: 0 });
        this.observer.observe(this.mainAddBtn);
      } else {
        // Fallback: reveal after the user scrolls down a bit.
        this._onScroll = function () { self.setVisible(window.scrollY > 600); };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._onScroll();
      }

      setHeight();
      window.addEventListener('resize', setHeight, { passive: true });
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      document.body.classList.toggle('sticky-atc-active', visible);
      if (visible) {
        document.documentElement.style.setProperty('--sticky-atc-height', this.offsetHeight + 'px');
      }
    }

    /* ------------------------------------------------------------------ *
     * Shade / variant selection
     * ------------------------------------------------------------------ */
    bindShade() {
      var self = this;
      this.shadeSelectors.forEach(function (selector) {
        var toggle = selector.querySelector('[data-shade-toggle]');
        var list = selector.querySelector('[data-shade-list]');
        var pos = selector.dataset.optionPosition;

        toggle.addEventListener('click', function (ev) {
          ev.stopPropagation();
          var isOpen = selector.classList.contains('is-open');
          self.closeAllShades();
          if (!isOpen) {
            selector.classList.add('is-open');
            list.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
          }
        });

        Array.prototype.slice.call(list.querySelectorAll('[data-shade-option]')).forEach(function (opt) {
          opt.addEventListener('click', function () {
            self.selectOption(pos, opt.dataset.value);
            self.closeAllShades();
          });
        });
      });
    }

    _onDocClick(ev) {
      if (!this.contains(ev.target)) { this.closeAllShades(); }
    }

    closeAllShades() {
      this.shadeSelectors.forEach(function (selector) {
        selector.classList.remove('is-open');
        var list = selector.querySelector('[data-shade-list]');
        var toggle = selector.querySelector('[data-shade-toggle]');
        if (list) { list.hidden = true; }
        if (toggle) { toggle.setAttribute('aria-expanded', 'false'); }
      });
    }

    /**
     * Proxy a shade choice into the main product form. Radio swatches use
     * .checked; popout <select> options are backed by a hidden input whose
     * value we set. Either way a bubbling `change` lets ProductForm react.
     */
    selectOption(position, value) {
      var selector = '[data-single-option-selector][data-index="option' + position + '"]';
      var inputs = Array.prototype.slice.call(this.productEl.querySelectorAll(selector));
      var matched = null;

      inputs.forEach(function (input) {
        if (input.type === 'radio') {
          if (input.value === value) {
            input.checked = true;
            matched = input;
          }
        } else {
          input.value = value;
          matched = input;
        }
      });

      if (matched) {
        matched.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ------------------------------------------------------------------ *
     * Reflect main form state back into the bar
     * ------------------------------------------------------------------ */
    bindReflect() {
      var self = this;
      if (this.variantsContainer) {
        this.variantsContainer.addEventListener('change', function () {
          window.requestAnimationFrame(function () { self.syncFromMain(); });
        });
      }
      if (this.mainAddBtn) {
        this.btnObserver = new MutationObserver(function () { self.syncAddState(); });
        this.btnObserver.observe(this.mainAddBtn, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    syncFromMain() {
      var self = this;
      this.shadeSelectors.forEach(function (selector) {
        var pos = selector.dataset.optionPosition;
        var checked = self.productEl.querySelector('[data-single-option-selector][data-index="option' + pos + '"]:checked')
          || self.productEl.querySelector('input[type="hidden"][data-single-option-selector][data-index="option' + pos + '"]');
        if (!checked) { return; }

        var value = checked.value;
        var valueEl = selector.querySelector('[data-shade-value]');
        if (valueEl) { valueEl.textContent = value; }

        // Reuse the matching option's rendered swatch style for a pixel-accurate
        // swatch. Match by dataset value (robust to spaces/parens in the name).
        var swatch = selector.querySelector('[data-shade-swatch]');
        var matchOption = null;
        Array.prototype.slice.call(selector.querySelectorAll('[data-shade-option]')).forEach(function (opt) {
          if (opt.dataset.value === value) { matchOption = opt; }
        });
        var optionEl = matchOption ? matchOption.querySelector('.sticky-atc__swatch') : null;
        if (swatch && optionEl) {
          swatch.setAttribute('style', optionEl.getAttribute('style'));
        }

        Array.prototype.slice.call(selector.querySelectorAll('[data-shade-option]')).forEach(function (opt) {
          var active = opt.dataset.value === value;
          opt.classList.toggle('is-active', active);
          if (active) {
            opt.setAttribute('aria-selected', 'true');
          } else {
            opt.removeAttribute('aria-selected');
          }
        });
      });

      this.syncAddState();
    }

    syncAddState() {
      if (!this.mainAddBtn || !this.addBtn) { return; }
      var disabled = this.mainAddBtn.hasAttribute('disabled');
      this.addBtn.toggleAttribute('disabled', disabled);

      var mainText = this.mainAddBtn.querySelector('[data-add-to-cart-text]');
      if (mainText && this.addText) {
        var text = mainText.textContent.trim();
        if (text) { this.addText.textContent = text; }
      }
    }

    /* ------------------------------------------------------------------ *
     * Quantity
     * ------------------------------------------------------------------ */
    bindQty() {
      var self = this;
      var minus = this.querySelector('[data-qty-minus]');
      var plus = this.querySelector('[data-qty-plus]');

      if (minus) { minus.addEventListener('click', function () { self.setQty(self.qty - 1); }); }
      if (plus) { plus.addEventListener('click', function () { self.setQty(self.qty + 1); }); }

      if (this.mainQtyInput) {
        var initial = parseInt(this.mainQtyInput.value, 10);
        if (!isNaN(initial) && initial > 0) { this.qty = initial; }
        this.mainQtyInput.addEventListener('change', function () {
          var mv = parseInt(self.mainQtyInput.value, 10);
          if (!isNaN(mv) && mv > 0 && mv !== self.qty) {
            self.qty = mv;
            self.renderQty();
          }
        });
      }
      this.renderQty();
    }

    setQty(next) {
      if (next < 1) { next = 1; }
      this.qty = next;
      this.renderQty();
      if (this.mainQtyInput) {
        this.mainQtyInput.value = next;
        this.mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    renderQty() {
      if (this.qtyValueEl) { this.qtyValueEl.textContent = this.qty; }
    }

    /* ------------------------------------------------------------------ *
     * Add to cart (proxy the main button)
     * ------------------------------------------------------------------ */
    bindAdd() {
      var self = this;
      if (!this.addBtn) { return; }
      this.addBtn.addEventListener('click', function () {
        if (self.addBtn.hasAttribute('disabled') || !self.mainAddBtn) { return; }

        // Make sure the main form carries the bar's quantity before submitting.
        if (self.mainQtyInput && parseInt(self.mainQtyInput.value, 10) !== self.qty) {
          self.mainQtyInput.value = self.qty;
          self.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        self.addBtn.classList.add('is-loading');
        self.mainAddBtn.click();
        window.setTimeout(function () { self.addBtn.classList.remove('is-loading'); }, 1500);
      });
    }
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyATC);
  }
})();
